import { useState, useEffect, useCallback, useRef } from 'react';

interface UseIdleDetectionOptions {
  idleTime?: number; // Time in ms before considered idle (default: 30000)
  onIdle?: () => void;
  onActive?: () => void;
}

interface UseIdleDetectionReturn {
  isIdle: boolean;
  idleDuration: number; // How long user has been idle in ms
  resetIdle: () => void;
}

/**
 * Custom hook to detect user inactivity
 * Listens to mouse, keyboard, scroll, and touch events
 */
export const useIdleDetection = (options: UseIdleDetectionOptions = {}): UseIdleDetectionReturn => {
  const { idleTime = 30000, onIdle, onActive } = options;
  
  const [isIdle, setIsIdle] = useState(false);
  const [idleDuration, setIdleDuration] = useState(0);
  
  const idleTimerRef = useRef<NodeJS.Timeout | null>(null);
  const durationIntervalRef = useRef<NodeJS.Timeout | null>(null);
  const idleStartRef = useRef<number | null>(null);
  const wasIdleRef = useRef(false);

  const resetIdle = useCallback(() => {
    // Clear existing timers
    if (idleTimerRef.current) {
      clearTimeout(idleTimerRef.current);
    }
    if (durationIntervalRef.current) {
      clearInterval(durationIntervalRef.current);
    }

    // If was idle, trigger onActive callback
    if (wasIdleRef.current) {
      wasIdleRef.current = false;
      setIsIdle(false);
      setIdleDuration(0);
      idleStartRef.current = null;
      onActive?.();
    }

    // Set new idle timer
    idleTimerRef.current = setTimeout(() => {
      wasIdleRef.current = true;
      setIsIdle(true);
      idleStartRef.current = Date.now();
      onIdle?.();

      // Start tracking duration
      durationIntervalRef.current = setInterval(() => {
        if (idleStartRef.current) {
          setIdleDuration(Date.now() - idleStartRef.current);
        }
      }, 1000);
    }, idleTime);
  }, [idleTime, onIdle, onActive]);

  useEffect(() => {
    // Events that indicate user activity
    const events = [
      'mousemove',
      'mousedown',
      'keydown',
      'touchstart',
      'scroll',
      'wheel',
      'resize',
    ];

    // Throttle to avoid too many resets
    let lastReset = 0;
    const throttledReset = () => {
      const now = Date.now();
      if (now - lastReset > 100) {
        lastReset = now;
        resetIdle();
      }
    };

    // Add event listeners
    events.forEach(event => {
      window.addEventListener(event, throttledReset, { passive: true });
    });

    // Start initial timer
    resetIdle();

    // Cleanup
    return () => {
      events.forEach(event => {
        window.removeEventListener(event, throttledReset);
      });
      if (idleTimerRef.current) {
        clearTimeout(idleTimerRef.current);
      }
      if (durationIntervalRef.current) {
        clearInterval(durationIntervalRef.current);
      }
    };
  }, [resetIdle]);

  return { isIdle, idleDuration, resetIdle };
};

export default useIdleDetection;
